import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import dayjs from 'dayjs';
import { PayloadAction } from '@reduxjs/toolkit';

import { request } from '@app/services';
import {
  addAttachment,
  addAttachmentFailed,
  addAttachmentSuccess,
  addCard,
  addCardFailed,
  addCardMember,
  addCardMemberFailed,
  addCardMemberSuccess,
  addCardSuccess,
  addCoverImage,
  addCoverImageFailed,
  addCoverImageSuccess,
  addDate,
  addDateFailed,
  addDateSuccess,
  addDescription,
  addDescriptionFailed,
  addDescriptionSuccess,
  addList,
  addListFailed,
  addListSuccess,
  addTaskBoard,
  addTaskBoardFailed,
  addTaskBoardSuccess,
  deleteCardByID,
  deleteCardByIDSuccess,
  deleteCardsByIDFailed,
  deleteListByID,
  deleteListByIDFailed,
  deleteListByIDSuccess,
  getBoardDetails,
  getBoardDetailsFailed,
  getBoardDetailsSuccess,
  getCardDetailsByID,
  getCardDetailsByIDFailed,
  getCardDetailsByIDSuccess,
  getTaskBoard,
  getTaskBoardFailed,
  getTaskBoardSuccess,
  moveCard,
  moveCardFailed,
  moveCardSuccess,
  getSearchMember,
  getSearchMemberFailed,
  getSearchMemberSuccess,
  getSearchBoard,
  getSearchBoardFailed,
  getSearchBoardSuccess,
  deleteAttacmentByIDSuccess,
  deleteAttacmentByIDFailed,
  deleteAttacmentByID,
  getBackground,
  getBackgroundFailed,
  getBackgroundSuccess,
  updateBoard,
  updateBoardFailed,
  updateBoardSuccess,
  addMember,
  addMemberFailed,
  addMemberSuccess,
  deleteMember,
  deleteMemberFailed,
  deleteMemberSuccess,
  updateDate,
  updateDateFailed,
  updateDateSuccess,
  deleteMemberFromCard,
  deleteMemberFromCardFailed,
  deleteMemberFromCardSuccess,
  updateMember,
  updateMemberFailed,
  updateMemberSuccess,
  addLabel,
  addLabelFailed,
  addLabelSuccess,
  deleteLabel,
  deleteLabelFailed,
  deleteLabelSuccess,
  updateLabel,
  updateLabelFailed,
  updateLabelSuccess,
  getLabels,
  getLabelsFailed,
  getLabelsSuccess,
  updateCardTitleSuccess,
  updateCardTitle,
  updateCardTitleFailed,
  getComments,
  getCommentsSuccess,
  getCommentsFailed,
  addComments,
  addCommentsSuccess,
  addCommentsFailed,
  deleteCommentByID,
  deleteCommentByIDSuccess,
  deleteCommentByIDFailed,
  toggleLabel,
  toggleLabelSuccess,
  toggleLabelFailed,
  deleteDate,
  deleteDateSuccess,
  deleteDateFailed,
  updateCommentByID,
  updateCommentByIDFailed,
  updateCommentByIDSuccess,
  deleteCheckList,
  addCheckList,
  addCheckListSuccess,
  addCheckListFailed,
  deleteCheckListSuccess,
  deleteCheckListFailed,
  getChecklist,
  getChecklistSuccess,
  getChecklistFailed,
  updateCheckList,
  updateCheckListFailed,
  updateCheckListSuccess,
  addCheckListItemSuccess,
  addCheckListItem,
  addCheckListItemFailed,
  getCheckListItemSuccess,
  getCheckListItem,
  getCheckListItemFailed,
  updateItemStatusSuccess,
  updateItemStatusFailed,
  updateItemStatus,
  deleteCheckListItem,
  deleteCheckListItemSuccess,
  deleteCheckListItemFailed,
  replyComment,
  replyCommentSuccess,
  replyCommentFailed,
  getTransaction,
  getTransactionSuccess,
  getTransactionFailed,
  addReaction,
  addReactionSuccess,
  addReactionFailed,
} from './slice';
import * as endpoints from './endpoints';
import { RootState } from '@app/redux/root-reducer';
import { setSelectedProjectId } from '../common';
import { selectCardDetailState, selectCommentsState } from './selector';
import { IChecklist, IChecklistItem } from './types';
import { getMyTaskDetailsSuccess } from '../tasks/slice';
import { selectMyTaskDetailsState } from '../tasks/selector';

function* getTaskBoardSaga(action: ReturnType<typeof getTaskBoard>): any {
  try {
    const { userId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getTaskBoard}/${userId}`);
    yield put(getTaskBoardSuccess(result.data.records));
  } catch (err) {
    yield put(getTaskBoardFailed(err));
  }
}
//
function* addTaskBoardSaga(action: ReturnType<typeof addTaskBoard>): any {
  try {
    const { description, image, projectId, boardName } = action.payload;
    const { data } = yield select((store: RootState) => store.taskBoardReducer.taskBoard);

    const formData = new FormData();
    formData.append('name', boardName);
    formData.append('projectId', projectId);
    formData.append('description', description);
    formData.append('image', image);

    const result = yield call(request.post, endpoints.addTaskBoard, formData);
    const getTaskBoardData: any = [result?.data?.board, ...data];
    yield put(addTaskBoardSuccess(result.data));
    yield put(getTaskBoardSuccess(getTaskBoardData));
    yield put(setSelectedProjectId(result?.data?.board?.id));
  } catch (err) {
    yield put(addTaskBoardFailed(err));
  }
}

//
function* addListSaga(action: ReturnType<typeof addList>): any {
  try {
    const { name, boardId, showInTaskOverView, listId, listIndex } = action.payload;

    const listData = {
      name,
      boardId,
      showInTaskOverView,
      listIndex,
    };
    let result;
    if (listId) {
      result = yield call(request.put, `${endpoints.updateList}${listId}`, listData);
    } else {
      result = yield call(request.post, endpoints.addList, listData);
    }
    yield put(addListSuccess(result.data.message));
    yield put(getBoardDetails(boardId));
  } catch (err) {
    yield put(addListFailed(err));
  }
}
//
function* addCardSaga(action: ReturnType<typeof addCard>): any {
  try {
    const { name, boardId, listId } = action.payload;

    const result = yield call(request.post, endpoints.addCard, {
      name,
      boardId,
      listId,
    });
    yield put(addCardSuccess(result.data));
    yield put(getBoardDetails(boardId));
  } catch (err) {
    yield put(addCardFailed(err));
  }
}
//
function* updateCardTitleSaga(action: ReturnType<typeof updateCardTitle>): any {
  const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
  try {
    const { cardId, name, description } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateCardTitle}${cardId}`, {
      name,
      cardId,
      description: description ?? '',
    });
    const updatedDetails = { ...data, name: result?.data?.card?.name };
    yield put(updateCardTitleSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
  } catch (err) {
    yield put(updateCardTitleFailed(err));
  }
}

//
function* adddDatesSaga(action: ReturnType<typeof addDate>): any {
  try {
    const { startDate, endDate, isCompleted, status, cardId, endTime, id, startTime } =
      action.payload;

    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);

    const formattedStartDate = startDate ? dayjs(startDate).format('YYYY-MM-DDTHH:mm:ssZ') : '';
    const formattedEndDate = endDate ? dayjs(endDate).format('YYYY-MM-DDTHH:mm:ssZ') : '';
    const formattedEndTime = endTime ? dayjs(endTime).format('YYYY-MM-DDTHH:mm:ssZ') : '';
    const formattedStartTime = startTime ? dayjs(startTime).format('YYYY-MM-DDTHH:mm:ssZ') : '';

    let result;
    if (id) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      result = yield call(request.put, `${endpoints.addDate}/${id}`, {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        endTime: formattedEndTime,
        startTime: formattedStartTime,
        isCompleted,
        status,
      });
    } else {
      result = yield call(request.post, endpoints.addDate, {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        endTime: formattedEndTime,
        startTime: formattedStartTime,
        isCompleted,
        cardId,
      });
    }

    const updatedDetails = { ...data, dueDates: result?.data?.date };

    yield put(addDateSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(addDateFailed(err));
  }
}
//
function* getBoardDetailSaga(action: ReturnType<typeof getBoardDetails>): any {
  try {
    const boardId = action.payload;
    // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getBoardDetails}${boardId}`);
    yield put(setSelectedProjectId(result?.data?.id));
    yield put(getBoardDetailsSuccess(result.data));
  } catch (err) {
    yield put(getBoardDetailsFailed(err));
  }
}
//
function* addAttachmentSaga(action: ReturnType<typeof addAttachment>): any {
  try {
    const { attachments, cardId } = action.payload;

    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);

    const formData = new FormData();
    if (attachments && attachments.length > 0) {
      [...attachments].forEach((image: any, index: number) => {
        formData.append(`attachments`, image);
      });
    }
    formData.append('cardId', String(cardId));
    const result = yield call(request.post, endpoints.uploadDocuments, formData);
    const updatedDetails = {
      ...data,
      attachments: [...data.attachments, ...result.data.attachments],
    };
    yield put(addAttachmentSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(addAttachmentFailed(err));
  }
}
//
function* getCardDetailsByIdSaga(action: ReturnType<typeof getCardDetailsByID>): any {
  try {
    const { userId, boardId, listId, cardId } = action.payload;
    const result = yield call(
      request.get,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${endpoints.getCardDetail}${userId}/${boardId}/${listId}/${cardId}`
    );
    yield put(getCardDetailsByIDSuccess(result.data));
  } catch (err) {
    yield put(getCardDetailsByIDFailed(err));
  }
}
//
function* moveCardSaga(action: ReturnType<typeof moveCard>): any {
  try {
    const { boardId, newListId, existingListId, cardId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const result = yield call(
      request.put,
      `${endpoints.moveCard}${boardId}/${existingListId}/${cardId}/listId`,
      {
        newListId,
      }
    );
    yield put(moveCardSuccess(result.data.message));
    yield put(getBoardDetails(boardId));
  } catch (err) {
    yield put(moveCardFailed(err));
  }
}
//
//
function* addCardMemberSaga(action: ReturnType<typeof addCardMember>): any {
  try {
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);

    const result = yield call(request.post, endpoints.addCardMember, action.payload);

    const updatedDetails = {
      ...data,
      members: [...result?.data?.members],
    };

    yield put(addCardMemberSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(addCardMemberFailed(err));
  }
}
//
function* addCoverImageSaga(action: ReturnType<typeof addCoverImage>): any {
  try {
    const { attachmentId, isCoverImage } = action.payload;
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);

    // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.addCoverImage}${attachmentId}`, {
      isCoverImage,
    });

    const updatedDetail = {
      ...data,
      attachments: data?.attachments?.map((attachment: any) => ({
        ...attachment,
        isCoverImage:
          attachment?.id === result?.data?.card?.id ? result?.data?.card?.isCoverImage : false,
      })),
    };

    yield put(addCoverImageSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetail));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(addCoverImageFailed(err));
  }
}
//
function* deleteCardByIdSaga(action: ReturnType<typeof deleteCardByID>): any {
  try {
    const { boardId, listId, cardId } = action.payload;
    const result = yield call(
      request.delete,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${endpoints.deleteCard}${boardId}/${listId}/${cardId}`
    );
    yield put(deleteCardByIDSuccess(result.data));
    yield put(getBoardDetails(Number(boardId)));
  } catch (err) {
    yield put(deleteCardsByIDFailed(err));
  }
}
//
function* deleteListByIdSaga(action: ReturnType<typeof deleteListByID>): any {
  try {
    const { listId } = action.payload;
    const result = yield call(request.delete, endpoints.deleteList + listId);
    yield put(deleteListByIDSuccess(result.data));
    yield put(getBoardDetails(result?.data?.list?.boardId));
  } catch (err) {
    yield put(deleteListByIDFailed(err));
  }
}
//
function* addDescriptionSaga(action: ReturnType<typeof addDescription>): any {
  try {
    const { cardId, description, name } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.moveCard}${cardId}`, {
      description,
      name,
    });

    yield put(addDescriptionSuccess(result.data));
  } catch (err) {
    yield put(addDescriptionFailed(err));
  }
}
//
function* getSearchMemberSaga(action: ReturnType<typeof getSearchMember>): any {
  try {
    const { name, boardId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.searchMember}${boardId}`, {
      params: {
        name,
      },
    });
    yield put(getSearchMemberSuccess(result.data));
  } catch (err) {
    yield put(getSearchMemberFailed(err));
  }
}
//
function* getSearchBoardSaga(action: ReturnType<typeof getSearchBoard>): any {
  try {
    const { name, userId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.searchBoard}${userId}`, {
      params: {
        name,
      },
    });
    yield put(getSearchBoardSuccess(result.data));
  } catch (err) {
    yield put(getSearchBoardFailed(err));
  }
}
function* deleteAttachmentByIdSaga(action: ReturnType<typeof deleteAttacmentByID>): any {
  try {
    const { attachmentId } = action.payload;

    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const result = yield call(request.delete, endpoints.deleteAttachment + attachmentId);

    const updatedAttachments = {
      ...data,
      attachments: data?.attachments?.filter((attachment: any) => attachment?.id !== attachmentId),
    };
    yield put(deleteAttacmentByIDSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedAttachments));
  } catch (err) {
    yield put(deleteAttacmentByIDFailed(err));
  }
}
//
function* getBackgroundSaga(action: ReturnType<typeof getBackground>): any {
  try {
    const result = yield call(request.get, endpoints.getBackground);
    yield put(getBackgroundSuccess(result.data.records));
  } catch (err) {
    yield put(getBackgroundFailed(err));
  }
}
//
function* addMemberSaga(action: ReturnType<typeof addMember>): any {
  try {
    const { membersUserIds, boardId } = action.payload;
    const result = yield call(request.post, endpoints.addMember, {
      boardId,
      membersUserIds,
    });
    yield put(addMemberSuccess(result.data.message));
    yield put(getBoardDetails(boardId));
  } catch (err) {
    yield put(addMemberFailed(err));
  }
}
//
function* deleteMemberSaga(action: ReturnType<typeof deleteMember>): any {
  try {
    const { userId, boardId } = action.payload;
    const result = yield call(
      request.delete,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${endpoints.deleteMember}${userId}/${boardId}`
    );
    yield put(deleteMemberSuccess(result.data.message));
    yield put(getBoardDetails(boardId));
    yield put(getSearchMember(boardId));
  } catch (err) {
    yield put(deleteMemberFailed(err));
  }
}
//
function* updateBoardSaga(action: ReturnType<typeof updateBoard>): any {
  try {
    const { boardId, image, boardName, description } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const formData = new FormData();
    formData.append('name', boardName);
    formData.append('boardId', boardId);
    formData.append('description', description);
    formData.append('image', image);

    const result = yield call(request.put, `${endpoints.updateBoard}${String(boardId)}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    yield put(updateBoardSuccess(result.data));
    yield put(getBoardDetails(boardId));
  } catch (err) {
    yield put(updateBoardFailed(err));
  }
}
//
function* updateDateSaga(action: ReturnType<typeof updateDate>): any {
  try {
    const { startTime, startDate, endDate, isCompleted, cardId, endTime, dateId, status } =
      action.payload;

    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);

    const formattedStartTime = startTime ? dayjs(startTime).format('YYYY-MM-DDTHH:mm:ssZ') : null;
    const formattedStartDate = startDate ? dayjs(startDate).format('YYYY-MM-DDTHH:mm:ssZ') : '';
    const formattedEndDate = endDate ? dayjs(endDate).format('YYYY-MM-DDTHH:mm:ssZ') : '';
    const formattedEndTime = endTime ? dayjs(endTime).format('YYYY-MM-DDTHH:mm:ssZ') : null;

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateDate}${dateId}`, {
      startTime: formattedStartTime,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      endTime: formattedEndTime,
      isCompleted,
      cardId,
      status,
    });

    const updatedDetails = { ...data, dueDates: result?.data?.date };

    yield put(updateDateSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(updateDateFailed(err));
  }
}
//
function* deleteDateSaga(action: ReturnType<typeof deleteDate>): any {
  const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
  try {
    const { dateId } = action.payload;
    const result = yield call(request.delete, `${endpoints.deleteDate}${dateId}`);
    yield put(deleteDateSuccess(result.data));
    yield put(getBoardDetails(data?.boardId));
  } catch (error) {
    yield put(deleteDateFailed(error));
  }
}

//
function* deleteMemberFromCardSaga(action: ReturnType<typeof deleteMemberFromCard>): any {
  try {
    const { userId, cardId } = action.payload;
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
    const result = yield call(
      request.delete,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${endpoints.deleteMemberFromCard}${userId}/${cardId}`
    );
    const getTaskBoardData = {
      ...data,
      members:
        data?.members?.filter(
          (member: any) => member.membersUserId !== result?.data?.member?.membersUserId
        ) || [],
    };
    yield put(deleteMemberFromCardSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(getTaskBoardData));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(deleteMemberFromCardFailed(err));
  }
}
//
function* toggleLabelSaga(action: ReturnType<typeof toggleLabel>): any {
  try {
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
    const { labelId, cardId } = action.payload;
    const result = yield call(
      request.patch,
      `${endpoints.toggleLabel}${cardId}?labelId=${labelId}`
    );
    yield put(getCardDetailsByIDSuccess({ ...data, addedLabels: result?.data?.addedLabels }));
    yield put(getBoardDetails(data?.boardId));
    yield put(toggleLabelSuccess(result.data));
  } catch (err) {
    yield put(toggleLabelFailed(err));
  }
}
//
function* updateMemberSaga(action: ReturnType<typeof updateMember>): any {
  try {
    const { boardId, userId, isAdmin } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateMember}${userId}/${boardId}`, {
      isAdmin,
    });
    yield put(updateMemberSuccess(result.data));
    yield put(getBoardDetails(boardId));
  } catch (err) {
    yield put(updateMemberFailed(err));
  }
}

function* addLabelSaga(action: ReturnType<typeof addLabel>): any {
  try {
    const { title, color, boardId } = action.payload;
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
    const result = yield call(request.post, endpoints.addLabel, {
      name: title,
      color,
      isAdded: true,
      boardId,
    });
    const updatedDetails = {
      ...data,
      labels: [...data.addedLabels, result.data.label],
    };

    yield put(addLabelSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(addLabelFailed(err));
  }
}
//
function* deleteLabelSaga(action: ReturnType<typeof deleteLabel>): any {
  try {
    const { labelId } = action.payload;
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
    const result = yield call(request.delete, `${endpoints.deleteLabel}${labelId} `);
    const getTaskBoardData = {
      ...data,
      addedLabels:
        data?.addedLabels?.filter((label: any) => label.id !== result?.data?.labelId) || [],
    };
    yield put(deleteLabelSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(getTaskBoardData));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(deleteLabelFailed(err));
  }
}
//
function* updateLabelSaga(action: ReturnType<typeof updateLabel>): any {
  try {
    const { id, title, color, isAdded } = action.payload;
    const { data } = yield select((store: RootState) => store.taskBoardReducer.cardDetailsById);
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateLabel}${id}`, {
      name: title,
      color,
      isAdded: isAdded || false,
    });
    const updatedDetails = {
      ...data,
      labels: data.labels.map((label: any) =>
        label.id === id ? { ...label, title, color, isAdded } : label
      ),
    };
    yield put(updateLabelSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedDetails));
    yield put(getBoardDetails(data?.boardId));
  } catch (err) {
    yield put(updateLabelFailed(err));
  }
}
//
function* getLabelsSaga(action: ReturnType<typeof getLabels>): any {
  try {
    const { boardId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getLabels}${boardId}`);
    yield put(getLabelsSuccess(result.data));
  } catch (err) {
    yield put(getLabelsFailed(err));
  }
}
//
function* getCommentsSaga(action: ReturnType<typeof getComments>): any {
  const { cardId } = action.payload;
  try {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getComments}${cardId}`);
    yield put(getCommentsSuccess(result.data));
  } catch (err) {
    yield put(getCommentsFailed(err));
  }
}
//
function* addCommentsSaga(action: ReturnType<typeof addComments>): any {
  try {
    const { comment, cardId, mentionedUserIds } = action.payload;

    const result = yield call(request.post, endpoints.addComment, {
      name: comment,
      mentionedUserIds,
      cardId,
    });

    const newComment = {
      ...result.data.comment,
      firstName: result.data.comment?.userId?.firstName ?? '',
      lastName: result.data.comment?.userId?.lastName ?? '',
      profileImage: result.data.comment?.userId?.profileImage ?? null,
      itSelf: result.data.comment?.itself ?? false,
    };

    yield put(addCommentsSuccess({ ...result.data, comment: newComment }));

    const taskDetailData = yield select((store: RootState) => store.myTasksReducer.myTaskDetails);

    if (taskDetailData?.data) {
      yield put(
        getMyTaskDetailsSuccess({
          ...taskDetailData.data,
          comments: [...(taskDetailData.data.comments || []), newComment],
        })
      );
    }
  } catch (err) {
    yield put(addCommentsFailed(err));
  }
}
//
function* deleteCommentByIdSaga(action: ReturnType<typeof deleteCommentByID>): any {
  try {
    const { commentId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.delete, `${endpoints.deleteComment}${commentId}`);
    yield put(deleteCommentByIDSuccess(result.data));
    const commentsData = yield select((store: RootState) => store.taskBoardReducer.getComments);
    const updatedCommentsList = commentsData?.data?.comments
      ?.map((comment: any) => {
        return {
          ...comment,
          replies: comment.replies?.filter((reply: any) => reply.id !== Number(commentId)),
        };
      })
      ?.filter((comment: any) => comment.id !== Number(commentId));
    yield put(
      getCommentsSuccess({
        ...commentsData.data,
        comments: updatedCommentsList,
      })
    );
    const taskDetailData = yield select((store: RootState) => store.myTasksReducer.myTaskDetails);
    const updatedTaskDetailComments = taskDetailData?.data?.comments
      ?.map((comment: any) => {
        return {
          ...comment,
          replies: comment?.replies?.filter((reply: any) => reply?.id !== Number(commentId)),
        };
      })
      ?.filter((comment: any) => comment.id !== Number(commentId));

    yield put(
      getMyTaskDetailsSuccess({
        ...taskDetailData.data,
        comments: updatedTaskDetailComments,
      })
    );
  } catch (err) {
    yield put(deleteCommentByIDFailed(err));
  }
}

//
function* updateCommentSaga(action: ReturnType<typeof updateCommentByID>): any {
  try {
    const { commentId, name, mentionedUserIds } = action.payload;

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateComment}${commentId}`, {
      name,
      mentionedUserIds,
    });

    yield put(updateCommentByIDSuccess(result.data));

    const commentsData = yield select(selectCommentsState);
    const taskDetailData = yield select((store: RootState) => store.myTasksReducer.myTaskDetails);

    const oldComment = taskDetailData?.data?.comments?.find((c: any) => c.id === commentId);

    const updatedComment = {
      ...oldComment,
      ...result.data.comment,
      name,
    };

    const updatedTaskDetailComments = taskDetailData?.data?.comments?.map((c: any) =>
      c.id === commentId ? updatedComment : c
    );

    yield put(
      getMyTaskDetailsSuccess({
        ...taskDetailData.data,
        comments: updatedTaskDetailComments,
      })
    );

    const updatedComments = {
      ...commentsData,
      data: {
        ...commentsData.data,
        comments: commentsData?.data?.comments?.map((c: any) =>
          c.id === commentId
            ? {
                ...c,
                ...result.data.comment,
                name,
                userId: c.userId ?? result.data.comment?.userId,
              }
            : c
        ),
      },
    };
    yield put(getCommentsSuccess(updatedComments.data));
  } catch (err) {
    yield put(updateCommentByIDFailed(err));
  }
}
//
function* replyCommentSaga(action: ReturnType<typeof replyComment>): any {
  try {
    const { commentId, name }: { commentId: string | number; name: string } = action.payload;
    const result = yield call(request.post, `${endpoints.replyComment}${commentId}`, { name });
    yield put(replyCommentSuccess(result.data));
  } catch (err) {
    yield put(replyCommentFailed(err));
  }
}

//
function* addCheckListSaga(action: ReturnType<typeof addCheckList>): any {
  try {
    const { cardId, isCompleted, text } = action.payload;
    const result = yield call(request.post, endpoints.addCheckList, {
      text,
      isCompleted,
      cardId,
    });
    const { data } = yield select(selectCardDetailState);
    const updatedData = {
      ...data,
      checklists:
        data?.checklists && data?.checklists?.length > 0
          ? [...data.checklists, result.data.checklist]
          : [result.data.checklist],
    };
    yield put(addCheckListSuccess(result.data));
    yield put(getCardDetailsByIDSuccess(updatedData));
  } catch (err) {
    yield put(addCheckListFailed(err));
  }
}
//
function* deleteCheckListSaga(action: ReturnType<typeof deleteCheckList>): any {
  try {
    const { checkListId } = action.payload;
    const result = yield call(request.delete, `${endpoints.deleteCheckList}${checkListId}`);
    yield put(deleteCheckListSuccess(result.data));
    const { data } = yield select(selectCardDetailState);
    const { data: taskDetailData } = yield select(selectMyTaskDetailsState);

    const updatedCardData = {
      ...data,
      checklists: data?.checklists?.filter((item: IChecklistItem) => item.id !== checkListId),
    };
    yield put(getCardDetailsByIDSuccess(updatedCardData));

    const updatedTaskDetailData = {
      ...taskDetailData,
      checklists: taskDetailData?.checklists?.filter(
        (item: IChecklistItem) => item.id !== checkListId
      ),
    };
    yield put(getMyTaskDetailsSuccess(updatedTaskDetailData));
  } catch (err: any) {
    yield put(deleteCheckListFailed(err));
  }
}

//
function* getCheckListSaga(action: ReturnType<typeof getChecklist>): any {
  try {
    const { cardId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getCheckList}${cardId}`);
    yield put(getChecklistSuccess(result.data));
  } catch (err) {
    yield put(getChecklistFailed(err));
  }
}
//
function* updateCheckListSaga(action: ReturnType<typeof updateCheckList>): any {
  try {
    const { checkListId, text, isCompleted } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateCheckList}${checkListId}`, {
      isCompleted,
      text,
    });
    yield put(updateCheckListSuccess(result.data));
  } catch (err) {
    yield put(updateCheckListFailed(err));
  }
}
//
function* addCheckListItemSaga(action: ReturnType<typeof addCheckListItem>): any {
  try {
    const { checkListId, title, isChecked } = action.payload;
    const response = yield call(request.post, `${endpoints.addCheckListItem}${checkListId}`, {
      title,
      isChecked,
    });
    const newItem = response?.data?.data;
    const { data: cardData } = yield select(selectCardDetailState);
    const { data: taskDetailData } = yield select(selectMyTaskDetailsState);

    const updatedChecklists = cardData?.checklists?.map((checklist: IChecklist) => {
      if (checklist.id === checkListId) {
        return {
          ...checklist,
          items: [...(checklist.items ?? []), newItem],
        };
      }
      return checklist;
    });
    const updatedCardData = {
      ...cardData,
      checklists: updatedChecklists,
    };
    yield put(getCardDetailsByIDSuccess(updatedCardData));
    const updatedTaskChecklists = taskDetailData?.checklists?.map((checklist: any) => {
      if (checklist.id === checkListId) {
        return {
          ...checklist,
          items: [...(checklist.items ?? []), newItem],
        };
      }
      return checklist;
    });

    const updatedTaskDetailData = {
      ...taskDetailData,
      checklists: updatedTaskChecklists,
    };

    yield put(getMyTaskDetailsSuccess(updatedTaskDetailData));
    yield put(addCheckListItemSuccess(response.data));
  } catch (err) {
    yield put(addCheckListItemFailed(err));
  }
}
//
function* getCheckListItemSaga(action: ReturnType<typeof getCheckListItem>): any {
  try {
    const { checklistId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getCheckListItem}${checklistId}`);
    yield put(getCheckListItemSuccess(result.data.data));
  } catch (err) {
    yield put(getCheckListItemFailed(err));
  }
}
//
function* updateItemStatusSaga(action: ReturnType<typeof updateItemStatus>): any {
  try {
    const { itemId, title, isChecked } = action.payload;
    const { data: cardData } = yield select(selectCardDetailState);
    const { data: taskDetailData } = yield select(selectMyTaskDetailsState);

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.put, `${endpoints.updateItemStatus}${itemId}`, {
      title,
      isChecked,
    });
    const updatedCheckList =
      cardData?.checklists && cardData?.checklists?.length > 0
        ? cardData?.checklists?.map((item: IChecklist) => {
            if (item?.id === result?.data?.data?.checklistId) {
              if (item?.items && item?.items?.length > 0) {
                return {
                  ...item,
                  items: item?.items?.map((i) => {
                    if (i?.id === result?.data?.data?.id) {
                      return result?.data?.data;
                    } else {
                      return i;
                    }
                  }),
                };
              } else {
                return item;
              }
            } else {
              return item;
            }
          })
        : [];

    yield put(getCardDetailsByIDSuccess({ ...cardData, checklists: updatedCheckList }));
    const updatedTaskChecklists =
      taskDetailData?.checklists && taskDetailData?.checklists?.length > 0
        ? taskDetailData?.checklists?.map((item: any) => {
            if (item?.id === result?.data?.data?.checklistId) {
              if (item?.items && item?.items?.length > 0) {
                return {
                  ...item,
                  items: item?.items?.map((i: { id: any }) => {
                    if (i?.id === result?.data?.data?.id) {
                      return result?.data?.data;
                    } else {
                      return i;
                    }
                  }),
                };
              } else {
                return item;
              }
            } else {
              return item;
            }
          })
        : [];

    const updatedTaskDetailData = {
      ...taskDetailData,
      checklists: updatedTaskChecklists,
    };
    yield put(getMyTaskDetailsSuccess(updatedTaskDetailData));
    yield put(updateItemStatusSuccess(result.data));
  } catch (err) {
    yield put(updateItemStatusFailed(err));
  }
}
//
function* deleteCheckListItemSaga(action: PayloadAction<{ itemId: number }>): any {
  try {
    const { itemId } = action.payload;

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.delete, `${endpoints.deleteCheckListItem}/${itemId}`);

    const { data: cardData } = yield select(selectCardDetailState);
    const { data: taskDetailData } = yield select(selectMyTaskDetailsState);

    const updatedChecklists = cardData?.checklists?.map((checklist: IChecklist) => ({
      ...checklist,
      items: checklist.items.filter((item: IChecklistItem) => item.id !== itemId),
    }));

    const updatedCardData = {
      ...cardData,
      checklists: updatedChecklists,
    };

    yield put(getCardDetailsByIDSuccess(updatedCardData));

    const updatedTaskChecklists = taskDetailData?.checklists?.map((checklist: any) => ({
      ...checklist,
      items: checklist.items.filter((item: IChecklistItem) => item.id !== itemId),
    }));
    const updatedTaskDetailData = {
      ...taskDetailData,
      checklists: updatedTaskChecklists,
    };

    yield put(getMyTaskDetailsSuccess(updatedTaskDetailData));
    yield put(deleteCheckListItemSuccess(result));
  } catch (err: any) {
    yield put(deleteCheckListItemFailed(err));
  }
}
//
function* getTransactionSaga(action: ReturnType<typeof getTransaction>): any {
  try {
    const { cardId } = action.payload;
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    const result = yield call(request.get, `${endpoints.getTransaction}${cardId}`);
    yield put(getTransactionSuccess(result.data));
  } catch (err) {
    yield put(getTransactionFailed(err));
  }
}
//
function* addReactionSaga(action: ReturnType<typeof addReaction>): any {
  try {
    const { commentId, reaction } = action.payload;

    const result = yield call(request.post, endpoints.addReaction + String(commentId), {
      type: reaction,
    });
    yield put(addReactionSuccess(result.data.message));
    const commentsState = yield select(selectCommentsState);
    let cardId = null;
    if (Array.isArray(commentsState.data.comments)) {
      const found = commentsState.data.comments.find(
        (c: any) => String(c.id) === String(commentId)
      );
      if (found.cardId) {
        cardId = found.cardId;
      }
    }
    if (cardId) {
      yield put(getComments({ cardId }));
    }
  } catch (err) {
    yield put(addReactionFailed(err));
  }
}

function* taskBoardSagas() {
  yield all([
    takeLatest(getTransaction, getTransactionSaga),
    takeLatest(updateCheckList, updateCheckListSaga),
    takeLatest(getChecklist, getCheckListSaga),
    takeLatest(addCheckList, addCheckListSaga),
    takeLatest(deleteCheckList, deleteCheckListSaga),
    takeLatest(replyComment, replyCommentSaga),
    takeLatest(updateCommentByID, updateCommentSaga),
    takeLatest(deleteCommentByID, deleteCommentByIdSaga),
    takeLatest(getComments, getCommentsSaga),
    takeLatest(addComments, addCommentsSaga),
    takeLatest([getTaskBoard], getTaskBoardSaga),
    takeLatest(addTaskBoard, addTaskBoardSaga),
    takeLatest([getBoardDetails], getBoardDetailSaga),
    takeLatest(addList, addListSaga),
    takeLatest(addCard, addCardSaga),
    takeLatest([updateCardTitle], updateCardTitleSaga),
    takeLatest(addAttachment, addAttachmentSaga),
    takeLatest(addDate, adddDatesSaga),
    takeLatest([getCardDetailsByID], getCardDetailsByIdSaga),
    takeLatest(moveCard, moveCardSaga),
    takeLatest(addCardMember, addCardMemberSaga),
    takeLatest(addCoverImage, addCoverImageSaga),
    takeLatest(deleteCardByID, deleteCardByIdSaga),
    takeLatest(deleteListByID, deleteListByIdSaga),
    takeLatest(addDescription, addDescriptionSaga),
    takeLatest([getSearchMember, deleteMemberSuccess], getSearchMemberSaga),
    takeLatest(getSearchBoard, getSearchBoardSaga),
    takeLatest(deleteAttacmentByID, deleteAttachmentByIdSaga),
    takeLatest(getBackground, getBackgroundSaga),
    takeLatest(updateBoard, updateBoardSaga),
    takeLatest(addMember, addMemberSaga),
    takeLatest(deleteMember, deleteMemberSaga),
    takeLatest(updateDate, updateDateSaga),
    takeLatest(deleteMemberFromCard, deleteMemberFromCardSaga),
    takeLatest(updateMember, updateMemberSaga),
    takeLatest(addLabel, addLabelSaga),
    takeLatest(deleteLabel, deleteLabelSaga),
    takeLatest(updateLabel, updateLabelSaga),
    takeLatest(getLabels, getLabelsSaga),
    takeLatest(toggleLabel, toggleLabelSaga),
    takeLatest(deleteDate, deleteDateSaga),
    takeLatest(addCheckListItem, addCheckListItemSaga),
    takeLatest(getCheckListItem, getCheckListItemSaga),
    takeLatest(updateItemStatus, updateItemStatusSaga),
    takeLatest(deleteCheckListItem, deleteCheckListItemSaga),
    takeLatest(addReaction, addReactionSaga),
  ]);
}
export { taskBoardSagas };
